import axios from "axios";
import Swal from "sweetalert2";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Turn a backend-relative image URL (e.g. "/api/uploads/items/xxx.png") into
// an absolute URL that the browser can fetch. Handles null/absolute URLs safely.
export function assetUrl(u) {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `${BACKEND_URL}${u.startsWith("/") ? "" : "/"}${u}`;
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  // No default Content-Type — let axios pick it per-request:
  //   • plain objects → "application/json" (axios default)
  //   • FormData      → "multipart/form-data; boundary=..." (browser default)
  // Setting "application/json" here globally was breaking every file upload
  // (item image, later CSVs) because axios never overrode our explicit
  // header, so multipart bodies were sent labelled as JSON → FastAPI
  // returned 422 with `loc: ["body", "file"]` "Field required".
});

// Set X-Industry header defensively both on the instance (survives HMR) and
// via the request interceptor (picks up any newer value from localStorage).
{
  const boot = typeof window !== "undefined" ? localStorage.getItem("industry") : null;
  if (boot) api.defaults.headers.common["X-Industry"] = boot;
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const industry = localStorage.getItem("industry");
  if (industry) config.headers["X-Industry"] = industry;
  // Defense-in-depth for multipart uploads: if callers ever explicitly pass
  // Content-Type=application/json (legacy code), strip it when the body is
  // a FormData so the browser injects the correct multipart boundary.
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    if (config.headers?.["Content-Type"]) delete config.headers["Content-Type"];
    if (config.headers?.common?.["Content-Type"]) delete config.headers.common["Content-Type"];
  }
  return config;
});

// Detect the special "account_deleted:<business>" 401 detail we mint in
// backend/auth.py when a super admin has deleted the tenant this session
// belongs to. Show a SweetAlert modal ONCE, wipe the local session, and
// redirect the user to /login. Regular 401s (bad password, expired token)
// are left alone so pages can keep their own toasts.
let _accountDeletedShown = false;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string" && detail.startsWith("account_deleted")) {
      if (!_accountDeletedShown) {
        _accountDeletedShown = true;
        const bizName = detail.split(":").slice(1).join(":").trim();
        try { localStorage.removeItem("token"); } catch { /* ignore */ }
        try { localStorage.removeItem("industry"); } catch { /* ignore */ }
        await Swal.fire({
          icon: "error",
          title: "Your account was deleted",
          html: bizName
            ? `The admin has removed the account for <b>${bizName}</b>. Please contact your administrator if this is unexpected.`
            : "The admin has removed your account. Please contact your administrator if this is unexpected.",
          confirmButtonText: "Back to login",
          confirmButtonColor: "#2563eb",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
        // Force a full page reload so all React contexts (Auth, industry) reset.
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  },
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => {
        if (!e || typeof e !== "object") return String(e);
        // Include the field path so users see "batches[0].batch_no is required"
        // instead of just "Field required" (which was the earlier UX bug).
        const loc = Array.isArray(e.loc)
          ? e.loc.filter((x) => x !== "body").join(".")
          : "";
        const msg = e.msg || JSON.stringify(e);
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join(" · ");
  return String(detail);
}

export const fmtINR = (v) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(v || 0));

export const fmtNum = (v) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(
    Number(v || 0),
  );
